import type { CreditBalance, StudioActionKind } from '../types/domain';

export type PricedStudioActionKind = StudioActionKind | 'tempo' | 'pitch';

/** One-time credits a newly verified non-admin account starts with. */
export const VERIFIED_SIGNUP_CREDITS = 120;

/** Credits reserved for one ordinary music generation request. */
export const MUSIC_GENERATION_CREDITS = 2;

/** Credits charged for one generated cover image. */
export const COVER_IMAGE_GENERATION_CREDITS = 1;

/** Credits charged for one successful Spotify Music DNA analysis. */
export const MUSIC_DNA_ANALYSIS_CREDITS = 1;

/** Text Music creates both a Lyria song and its generated cover. */
export const TEXT_MUSIC_GENERATION_CREDITS =
  MUSIC_GENERATION_CREDITS + COVER_IMAGE_GENERATION_CREDITS;

export type GenerationCreditSource = 'text-music' | 'image-music' | 'music-dna' | 'genre-mixer';

/** Server-owned generation prices for each creation workflow. */
export const GENERATION_CREDITS_BY_SOURCE: Record<GenerationCreditSource, number> = {
  'text-music': TEXT_MUSIC_GENERATION_CREDITS,
  'image-music': MUSIC_GENERATION_CREDITS,
  // Music DNA generation creates both a song and an automatic cover.
  'music-dna': TEXT_MUSIC_GENERATION_CREDITS,
  // Genre Mixer generation also creates an automatic cover.
  'genre-mixer': TEXT_MUSIC_GENERATION_CREDITS,
};

/** Credit cost of each Studio action. Shared by frontend confirmation and backend charging. */
export const STUDIO_ACTION_CREDITS: Record<PricedStudioActionKind, number> = {
  extend: 2,
  remix: 3,
  replace: 2,
  trim: 1,
  cut: 1,
  split: 1,
  crossfade: 1,
  insertSilence: 1,
  fade: 1,
  normalize: 1,
  tempo: 1,
  pitch: 1,
  mix: 2,
  master: 2,
  stems: 3,
  cover: 1,
  regenerate: 2,
};

/** Spendable credits left after subtracting what has been used and reserved. */
export function availableCredits(
  balance: Pick<CreditBalance, 'total' | 'used' | 'reserved'>
): number {
  return Math.max(0, balance.total - balance.used - (balance.reserved ?? 0));
}

function nonNegative(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
}

/** Rebuilds the compatibility totals from the independent monthly and top-up buckets. */
export function withCreditTotals(balance: CreditBalance): CreditBalance {
  const monthlyAllowance = nonNegative(balance.monthlyAllowance);
  const monthlyUsed = Math.min(monthlyAllowance, nonNegative(balance.monthlyUsed));
  const monthlyReserved = Math.min(
    Math.max(0, monthlyAllowance - monthlyUsed),
    nonNegative(balance.monthlyReserved)
  );
  const topUp = nonNegative(balance.topUp);
  const topUpUsed = Math.min(topUp, nonNegative(balance.topUpUsed));
  const topUpReserved = Math.min(
    Math.max(0, topUp - topUpUsed),
    nonNegative(balance.topUpReserved)
  );
  return {
    ...balance,
    monthlyAllowance,
    monthlyUsed,
    monthlyReserved,
    topUp,
    topUpUsed,
    topUpReserved,
    total: monthlyAllowance + topUp,
    used: monthlyUsed + topUpUsed,
    reserved: monthlyReserved + topUpReserved,
  };
}

/** Converts a legacy aggregate-only balance into the two-bucket representation. */
export function normalizeCreditBuckets(balance: CreditBalance): CreditBalance {
  if (
    balance.monthlyAllowance !== undefined ||
    balance.topUp !== undefined ||
    balance.topUpUsed !== undefined
  ) {
    return withCreditTotals(balance);
  }
  return withCreditTotals({
    ...balance,
    monthlyAllowance: 0,
    monthlyUsed: 0,
    monthlyReserved: 0,
    topUp: nonNegative(balance.total),
    topUpUsed: nonNegative(balance.used),
    topUpReserved: nonNegative(balance.reserved),
  });
}

export function addCalendarMonths(iso: string, months: number): string {
  const source = new Date(iso);
  if (!Number.isFinite(source.getTime())) throw new Error('Invalid subscription timestamp.');
  const target = new Date(source);
  const day = source.getUTCDate();
  target.setUTCDate(1);
  target.setUTCMonth(target.getUTCMonth() + months);
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target.toISOString();
}

export function activateUnlimitedCredits(
  balance: CreditBalance,
  startedAt: string,
  monthlyAllowance: number
): CreditBalance {
  const current = normalizeCreditBuckets(balance);
  return withCreditTotals({
    ...current,
    unlimited: true,
    monthlyAllowance,
    monthlyUsed: 0,
    monthlyReserved: 0,
    resetsAt: addCalendarMonths(startedAt, 1),
    unlimitedStartedAt: new Date(startedAt).toISOString(),
    unlimitedExpiresAt: addCalendarMonths(startedAt, 12),
  });
}

/** Applies monthly expiry and annual deactivation before every authoritative balance read/write. */
export function refreshUnlimitedCredits(
  balance: CreditBalance,
  now = new Date()
): { balance: CreditBalance; changed: boolean } {
  const current = normalizeCreditBuckets(balance);
  if (!current.unlimited) return { balance: current, changed: false };
  const nowMs = now.getTime();
  const expiresMs = Date.parse(current.unlimitedExpiresAt ?? '');
  if (Number.isFinite(expiresMs) && nowMs >= expiresMs) {
    return {
      balance: withCreditTotals({
        ...current,
        unlimited: false,
        monthlyAllowance: 0,
        monthlyUsed: 0,
        monthlyReserved: 0,
        resetsAt: null,
      }),
      changed: true,
    };
  }
  const resetMs = Date.parse(current.resetsAt ?? '');
  if (!Number.isFinite(resetMs) || nowMs < resetMs) {
    return { balance: current, changed: false };
  }
  const startedAt = current.unlimitedStartedAt;
  if (!startedAt) return { balance: current, changed: false };
  let month = 1;
  let nextReset = addCalendarMonths(startedAt, month);
  while (Date.parse(nextReset) <= nowMs && month < 12) {
    month += 1;
    nextReset = addCalendarMonths(startedAt, month);
  }
  return {
    balance: withCreditTotals({
      ...current,
      monthlyUsed: 0,
      monthlyReserved: 0,
      resetsAt: nextReset,
    }),
    changed: true,
  };
}

export function reserveCreditBuckets(balance: CreditBalance, amount: number): CreditBalance {
  const current = normalizeCreditBuckets(balance);
  const monthlyAvailable = Math.max(
    0,
    (current.monthlyAllowance ?? 0) -
      (current.monthlyUsed ?? 0) -
      (current.monthlyReserved ?? 0)
  );
  const fromMonthly = Math.min(amount, monthlyAvailable);
  return withCreditTotals({
    ...current,
    monthlyReserved: (current.monthlyReserved ?? 0) + fromMonthly,
    topUpReserved: (current.topUpReserved ?? 0) + (amount - fromMonthly),
  });
}

export function commitCreditBuckets(balance: CreditBalance, amount: number): CreditBalance {
  const current = normalizeCreditBuckets(balance);
  const fromMonthly = Math.min(amount, current.monthlyReserved ?? 0);
  const fromTopUp = Math.min(amount - fromMonthly, current.topUpReserved ?? 0);
  return withCreditTotals({
    ...current,
    monthlyReserved: (current.monthlyReserved ?? 0) - fromMonthly,
    monthlyUsed: (current.monthlyUsed ?? 0) + fromMonthly,
    topUpReserved: (current.topUpReserved ?? 0) - fromTopUp,
    topUpUsed: (current.topUpUsed ?? 0) + fromTopUp,
  });
}

export function releaseCreditBuckets(balance: CreditBalance, amount: number): CreditBalance {
  const current = normalizeCreditBuckets(balance);
  const fromMonthly = Math.min(amount, current.monthlyReserved ?? 0);
  const fromTopUp = Math.min(amount - fromMonthly, current.topUpReserved ?? 0);
  return withCreditTotals({
    ...current,
    monthlyReserved: (current.monthlyReserved ?? 0) - fromMonthly,
    topUpReserved: (current.topUpReserved ?? 0) - fromTopUp,
  });
}

export function spendCreditBuckets(balance: CreditBalance, amount: number): CreditBalance {
  const reserved = reserveCreditBuckets(balance, amount);
  return commitCreditBuckets(reserved, amount);
}

export function refundCreditBucketSpend(
  balance: CreditBalance,
  monthlyAmount: number,
  topUpAmount: number
): CreditBalance {
  const current = normalizeCreditBuckets(balance);
  return withCreditTotals({
    ...current,
    monthlyUsed: Math.max(0, (current.monthlyUsed ?? 0) - monthlyAmount),
    topUpUsed: Math.max(0, (current.topUpUsed ?? 0) - topUpAmount),
  });
}
